import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";

export const useUserDropdown = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [usernameSlug, setUsernameSlug] = useState<string | null>(null);
  const adminCheckComplete = useRef(false);
  const pathname = usePathname();

  const isOnDashboard = pathname.includes("/dashboard");
  const isOnProfilePage =
    pathname.includes("/profile/") || pathname.includes("/u/");
  const isOnHomePage = pathname === "/";
  const isOnAdminPage = pathname.includes("/admin");
  const isOnSettingsPage = pathname.includes("/settings");
  const isOnNotificationsPage = pathname.includes("/notifications");
  const isOnSuperadminPage = pathname.includes("/superadmin");

  useEffect(() => {
    let isMounted = true;

    if (!user) {
      if (isMounted) {
        setIsAdmin(false);
        setIsSuperadmin(false);
        setUsername(null);
        setDisplayName(null);
        setAvatarUrl(null);
        setUsernameSlug(null);
        adminCheckComplete.current = false;
      }
      return;
    }

    const fetchUserProfile = async () => {
      if (user && !adminCheckComplete.current) {
        try {
          const { data: profileData, error } = await supabase
            .from("profiles")
            .select(
              "is_admin, is_superadmin, username, avatar_url, username_slug, display_name"
            )
            .eq("id", user.id)
            .single();

          if (!error && profileData) {
            if (isMounted) {
              setIsAdmin(!!profileData.is_admin);
              setIsSuperadmin(!!profileData.is_superadmin);
              setUsername(profileData.username);
              setDisplayName(profileData.display_name || profileData.username);
              setUsernameSlug(profileData.username_slug);

              if (
                profileData.avatar_url &&
                typeof profileData.avatar_url === "string" &&
                profileData.avatar_url.trim() !== ""
              ) {
                setAvatarUrl(profileData.avatar_url);
              } else {
                setAvatarUrl(null);
              }
              adminCheckComplete.current = true;
            }
          } else if (error) {
            console.error("Error fetching profile:", error);
            if (isMounted) {
              setAvatarUrl(null);
            }
          }
        } catch (err) {
          console.error("Error fetching user profile:", err);
          if (isMounted) {
            setAvatarUrl(null);
          }
        }
      }
    };

    fetchUserProfile();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const getInitials = () => {
    if (displayName) {
      return displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    } else if (username) {
      return username
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    } else if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  return {
    user,
    isAdmin,
    isSuperadmin,
    username,
    displayName,
    avatarUrl,
    usernameSlug,
    getInitials,
    isOnDashboard,
    isOnProfilePage,
    isOnHomePage,
    isOnAdminPage,
    isOnSettingsPage,
    isOnNotificationsPage,
    isOnSuperadminPage,
  };
};
